import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { DateTimeFormatter } from '../../shared/date-format';
import { fontFamilies } from '../../theme/text';
import { ThresholdEvent } from './threshold-bar-chart';

/**
 * Data passed from the parent component from the bar currently being hovered.
 */
export interface ThresholdTooltipData extends ThresholdEvent {
  /** x coordinate of the bar */
  x: number;
  /** y coordinate of the bar */
  y: number;
}

export interface ThresholdTooltipProps {
  data: ThresholdTooltipData;
  svgWidth: number;
}

const useStyles = makeStyles<Theme, { x: number; svgWidth: number }>((theme) => ({
  text: ({ x, svgWidth }) => ({
    fontFamily: fontFamilies.body,
    fontSize: 16,
    dominantBaseline: 'hanging',
    textAnchor: x < svgWidth / 2 ? 'start' : 'end'
  })
}));

/**
 *  ## Threshold Tooltip
 *
 * Render a tooltip in the threshold bar chart. Shown when hovering over a bar.
 */
const ThresholdTooltip = ({ data, svgWidth }: ThresholdTooltipProps) => {
  const classes = useStyles({ x: data.x, svgWidth });
  const dateRef = React.useRef<SVGTextElement>();
  const [dateRect, setDateRect] = React.useState<DOMRect>();
  const valueRef = React.useRef<SVGTextElement>();
  const [valueRect, setValueRect] = React.useState<DOMRect>();
  const thresholdRef = React.useRef<SVGTextElement>();
  const [thresholdRect, setThresholdRect] = React.useState<DOMRect>();

  React.useLayoutEffect(() => {
    if (dateRef.current) {
      setDateRect(dateRef.current.getBBox());
    }
  }, [dateRef, data]);

  React.useLayoutEffect(() => {
    if (valueRef.current) {
      setValueRect(valueRef.current.getBBox());
    }
  }, [valueRef, data]);

  React.useLayoutEffect(() => {
    if (thresholdRef.current) {
      setThresholdRect(thresholdRef.current.getBBox());
    }
  }, [thresholdRef, data]);

  return (
    <g transform={`translate(${data.x < svgWidth / 2 ? data.x + 20 : data.x - 10},${data.y})`}>
      {dateRect && (
        <rect
          x={dateRect.x - 5}
          y={dateRect.y}
          width={dateRect.width + 10}
          height={dateRect.height}
          fill="rgba(32,32,32,0.8)"
        />
      )}
      <text className={classes.text} ref={dateRef} fill="white">
        {DateTimeFormatter.format(data.date)}
      </text>
      {valueRect && (
        <rect
          x={valueRect.x - 5}
          y={valueRect.y + 22}
          width={valueRect.width + 10}
          height={valueRect.height}
          fill={data.isPassing ? '#1F82BF' : '#ad1f1f'}
        />
      )}
      <text transform={`translate(0, ${22})`} className={classes.text} ref={valueRef} fill="white">
        {`Value: ${data.amount}`}
      </text>
      {thresholdRect && (
        <rect
          x={thresholdRect.x - 5}
          y={thresholdRect.y + 44}
          width={thresholdRect.width + 10}
          height={thresholdRect.height}
          fill={data.isPassing ? '#1F82BF' : '#ad1f1f'}
        />
      )}
      <text
        transform={`translate(0, ${44})`}
        className={classes.text}
        ref={thresholdRef}
        fill="white"
      >
        {`Threshold: ${data.comparative} ${data.threshold}`}
      </text>
    </g>
  );
};

export default ThresholdTooltip;
